import { LocalDateTime } from 'js-joda';
import { SectionsTables } from 'types/tableBasedInventory';
import { StoreType } from '@tock/shared/utils/connect';
import { createSelector } from 'reselect';
import { flatTablesWithSlotsSelector } from 'selectors/routes/calendar/tablesWithSlotsBySectionFlatSelector';
import createCachedSelector from 're-reselect';

export type TableOccupancy = {
  [TableId: string]: TableStatus;
};

export type TableStatus = 'OCCUPIED' | 'AVAILABLE';

export type TableOccupancySelectorProps = {
  startDateTimeString: string | undefined | null;
  turnTimeMinutes: number;
};

export function computeTableOccupancy(
  flatTablesAndSections: SectionsTables,
  { startDateTimeString, turnTimeMinutes }: TableOccupancySelectorProps
): TableOccupancy {
  if (!startDateTimeString) {
    return {};
  }

  const startTime = LocalDateTime.parse(startDateTimeString);
  const endTime = startTime.plusMinutes(turnTimeMinutes);

  return flatTablesAndSections.reduce<TableOccupancy>((tableOccupancy, item) => {
    if (item.type !== 'section') {
      const { slots, id } = item;

      let tableStatus: TableStatus = 'AVAILABLE';
      for (let i = startTime; i.isBefore(endTime); i = i.plusMinutes(15)) {
        const { isOccupied } = slots[i.toString()] ?? {};
        if (isOccupied) {
          tableStatus = 'OCCUPIED';
          break;
        }
      }
      tableOccupancy[id] = tableStatus;
    }
    return tableOccupancy;
  }, {});
}

const getStartDateTime = (
  _state: StoreType,
  startDateTimeString: TableOccupancySelectorProps['startDateTimeString']
) => startDateTimeString;

const getTurnTimeMinutes = (
  _state: StoreType,
  _startDateTimeString: TableOccupancySelectorProps['startDateTimeString'],
  turnTimeMinutes: TableOccupancySelectorProps['turnTimeMinutes']
) => turnTimeMinutes;

/**
 * Testing this with TIMELINE_UNCACHED_CALENDAR_SQUARES to see overall impact
 * to long term heap size when we don't cache every time/turn time result.
 */
export const tableOccupancySelectorNoCache = createSelector(
  flatTablesWithSlotsSelector,
  getStartDateTime,
  getTurnTimeMinutes,
  (
    flatTablesAndSections: SectionsTables,
    startDateTimeString: string | null | undefined,
    turnTimeMinutes: number
  ) => computeTableOccupancy(flatTablesAndSections, { startDateTimeString, turnTimeMinutes })
);

export const tableOccupancySelector = createCachedSelector(
  flatTablesWithSlotsSelector,
  (_state: StoreType, props: TableOccupancySelectorProps) => props,
  computeTableOccupancy
)(
  (state: StoreType, props: TableOccupancySelectorProps) =>
    `${props.startDateTimeString}:${props.turnTimeMinutes}`
);
